"use client";

import { Link } from "@/i18n/navigation";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import { formatBusPrice } from "@/utils/bus/priceUtils";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

// analytics
import { useAnalyticsContext, TrackingEvent, ProductType } from "@/analytics";

/**
 * Generic route/deal card used across landing pages.
 * Accepts data shaped like:
 * {
 *   "OriginCityName": "Horasan",
 *   "DestinationCityName": "Sultanbeyli",
 *   "Price": 1000,
 *   "Currency": "TRY",
 *   "ImageURL": "https://...",
 *   "OutgoingDepartureDate": "2025-12-12T13:40:00",
 *   "RedirectUrl": "/bus-ticket/istanbul-ankara"
 * }
 */

export default function TravelDealCard({
  item,
  localizationNameSpace = "common",
  dataLayerPayload = null
}) {
  const { dispatch: trackingDispatch } = useAnalyticsContext();
  const { currency } = useCurrency();
  const t = useTranslationDetector(localizationNameSpace);
  const price = item?.Price ? formatBusPrice(item?.Price, currency) : null;

  const handleClick = () => {
    if (dataLayerPayload) {
      trackingDispatch({
        type: TrackingEvent.CTA_CLICKED,
        payload: {
          productType: ProductType.BUS,
          ...dataLayerPayload,
        },
      });
    }
  };

  if (!item) return null;

  return (
    <Link
      onClick={handleClick}
      href={item.RedirectUrl || "/"}
      className="flex shrink-0"
    >
      <div className="relative aspect-[294/253] w-[294px] overflow-hidden rounded-lg shadow-sm lg:w-[214px] xl:w-[294px]">
        <Image
          src={item?.ImageURL || ""}
          alt={`${item.OriginCityName || ""} - ${item.DestinationCityName || ""}`}
          fill
          className="object-cover"
          sizes="(max-width: 1024px) 294px, (max-width: 1280px) 214px, 294px"
        />
        <div className="gradient-overlay absolute inset-0" />
        <div className="absolute bottom-6 left-6 z-10 space-y-2 text-white">
          <div className="flex flex-row flex-wrap items-center gap-[6px] text-xl/[26px] font-semibold">
            <span>{item.OriginCityName}</span>
            <FontAwesomeIcon
              className="h-6 w-6"
              icon={byPrefixAndName.fak["half-right-arrow"]}
            />
            <span>{item.DestinationCityName}</span>
          </div>
          {item.Price && item.Currency && (
            <div className="flex flex-row flex-wrap gap-1">
              <span className="text-base font-semibold">
                {price}
              </span>
              <span className="text-base font-normal">
                {t("priceFrom")}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}